<template>
    <!--PLACEHOLDER {mesh} add refresh button to re-fetch mesh params.-->
    <!--PLACEHOLDER {mesh} interactively change center distance and allow it to be applied to the model.-->
    <viewer-tab ref="tab" title="Mesh Review" :icon="$icons.connectRatio" typename="Connection"
                v-loading="loadingMesh" element-loading-text="Meshing Gears"
                :url-help="$urls.HELP_REPORTS" :click-x="clickX"
                @opened="handleTabOpened" @active="handleTabActive">
        <div class="pane-grid-1-2">
            <div class="pane-1" v-loading="loadingPixi" element-loading-text="Meshing gears">
                <div>
                    <el-select
                            class="mesh-select"
                            size="small"
                            placeholder="Select gear mesh"
                            v-model="conn"
                            value-key="Id"
                            @change="changeConnection"
                            clearable
                            filterable
                            no-match-text="No matching meshes"
                            no-data-text="No meshes in model"
                            default-first-option>
                        <el-option
                                v-for="item in conns"
                                :key="item.Id"
                                :label="item.label"
                                :value="item">
                        </el-option>
                    </el-select>
                    <el-select
                            class="mesh-select"
                            size="small"
                            placeholder="Select plot type"
                            v-model="plotType"
                            @change="changePlotType"
                            :disabled="!mesh.Id">
                        <el-option
                                v-for="item in $options.plotTypes"
                                :key="item.type"
                                :label="item.label"
                                :value="item.type">
                        </el-option>
                    </el-select>
                </div>
                <div class="pixi-wrap">
                    <canvas ref="pixi" class="pixi"/>
                </div>
                <div v-show="!mesh.Id" id="pane-1-message">
                    <span class="d-block mb-1">Select a mesh from the menu</span>
                    <small class="text-info">Hint: scroll to animate mesh</small>
                </div>
            </div>
            <div class="pane-2 pane-2-chart chart-container">
                <canvas ref="plot"/>
                <el-slider id="slider-rotation"
                           v-model="rotater.value"
                           :show-tooltip="false"
                           :min="rotater.min"
                           :max="rotater.max"
                           :step="(rotater.max - rotater.min) / rotater.steps">
                </el-slider>
            </div>
            <div class="pane-3 pane-3-tables">
                <modeler-tables :tables="tables" :log="mesh.table.log"/>
            </div>
        </div>
    </viewer-tab>
</template>

<script>
  import * as PIXI from "../../config/pixi";
  import Chart from "chart.js";
  import ViewerTab from "../viewer/ViewerTab";
  import ModelerTables from "../_tables/ModelerTables";
  import {ResizeListener, sortByString} from "../../assets/js/utils";
  import {getObjectById} from "../../store/helpers";
  import math from "../../assets/js/math";
  import gear from "../../assets/js/gear";
  import mesh from "../../assets/js/mesh";
  import pixi from "../../assets/js/pixi/parts";
  import coord from "../../assets/js/coord";

  export default {
    name: "ReviewMesh",
    components: {ModelerTables, ViewerTab},
    props: {
      clickX: Function,
      openId: String,
      name: String,  // unique tab name
    },

    units: {
      angle: 'deg',
    },
    plot: {
      nullData: math.linspace(-5, 5, 44).map(x => {return {x: x, y: 0}}),
    },
    plotTypes: {
      contactLines: {
        type: 'contactLines',
        label: 'Contact lines',
      },
      contactLinesSum: {
        type: 'contactLinesSum',
        label: 'Sum of contact lines',
      },
      contactRatio: {
        type: 'contactRatio',
        label: 'Instantaneous contact ratios',
      },
      specificSliding: {
        type: 'specificSliding',
        label: 'Specific sliding',
      }
    },
    collapseNames: {
      mesh: ['Common', 'Gear1', 'Gear2']
    },
    drawTeeth: 7,

    data() {
      return {
        conn: null,
        conns: [],
        renderMath: true,

        // APOLLO
        mesh: {
          Id: '',
          part1: null,
          part2: null,
          // MESH DATA
          ratio: 1,
          contactPlaneLength: 0,
          backlashCircumferential: 0,
          workingPressureAngleTransverse: 0,
          workingPitchDiameter1: 0,
          workingPitchDiameter2: 0,
          sapDiameter1: 0,
          sapDiameter2: 0,
          eapDiameter1: 0,
          eapDiameter2: 0,
          // TABULAR DATA
          titleGear1: 'Gear 1',  // collapse section title
          titleGear2: 'Gear 2',  // collapse section title
          table: {
            log: [],
            common: [],
            gear1: [],
            gear2: [],
          }
        },
        loadingMesh: false,

        // GRAPHICS
        pixi: null,
        pixiGear1: null,
        pixiGear2: null,
        pixiLoA: null,
        rotate1: 0,
        rotate2: 0,
        rotater: {
          value: .001,  // non-zero to trigger pixi ticker
          min: 0,
          max: 1,
          steps: 100,
          cycles: 2,
          cyclesArr: [],
        },
        loadingPixi: false,
        resizeListener: null,

        // PLOT
        loa: null,
        chart: null,
        plotType: 'contactLines',
        plotCache: {
          contactLines: [],
          sumOfContactLines: [],
          contactRatioTransverse: [],
          contactRatioAxial: [],
          specificSliding1: [],
          specificSliding2: [],
        },
        plotRotaterCallback: () => {},
      }
    },
    computed: {
      tables() {
        return [{
          table: this.mesh.table.common,
          title: "Common"
        }, {
          table: this.mesh.table.gear1,
          title: this.mesh.titleGear1
        }, {
          table: this.mesh.table.gear2,
          title: this.mesh.titleGear2
        }]
      },
      internal() {
        // Checks if mesh of internal gearing.
        return this.mesh.ratio < 0;
      },
      rotaterValue() {
        // used to watch embedded prop
        return this.rotater.value;
      },
      rotaterStepSize() {
        return (this.rotater.max - this.rotater.min) / this.rotater.steps;
      },
    },
    watch: {
      rotaterValue(val) {
        this.plotRotaterCallback(val);
      },
    },

    mounted() {
      // Called every time the report is opened.
      // Also see @loaded event handler.

      // map cached connections to local state; sort the same as Feature Tree
      this.conns = this.$store.getters.getConnections(this.$requests.model.ConnectionType.RATIO).map(conn => {
        return {
          ...conn,
          label: this.$handlers.model.getConnLabel(conn),
        }
      });
      sortByString(this.conns, 'label');
    },
    methods: {
      // APOLLO
      async queryMesh(connection) {
        // API query to calculate gear mesh data, mapping the result to local state.
        // PLACEHOLDER {performance} improve performance throughout app by caching mesh data; create a getMesh query

        // calculate mesh properties
        const meshConns = await this.$store.dispatch('getMeshes', [connection]);
        const conn = meshConns[0];

        let ratio, contactPlaneLength, backlashCircumferential;
        let workingFacewidth, workingPressureAngleTransverse;
        let workingPitchDiameter1, workingPitchDiameter2, sapDiameter1, sapDiameter2, eapDiameter1, eapDiameter2;
        let part1Id, part2Id;

        if (conn.mesh) {
          const params = conn.mesh.params;

          // INTERNAL CONDITION
          let gear1, gear2;
          if (params.macro.ratio < 0 && params.gear1.working_pitch_diameter < 0) {
            // swap gears so that internal gear is always gear #2
            gear1 = params.gear2;  // external
            gear2 = params.gear1;  // internal
            part1Id = connection.parts[1].Id;
            part2Id = connection.parts[0].Id;
            ratio = 1 / params.macro.ratio;
          } else {
            gear1 = params.gear1;  // external
            gear2 = params.gear2;  // internal
            part1Id = connection.parts[0].Id;
            part2Id = connection.parts[1].Id;
            ratio = params.macro.ratio;
          }

          // COMMON PARAMS
          contactPlaneLength = params.macro.contact_plane_length;
          backlashCircumferential = params.macro.backlash_circumferential;
          workingFacewidth = params.macro.working_facewidth;
          workingPressureAngleTransverse = params.macro.working_pressure_angle_transverse;
          // GEAR PARAMS
          workingPitchDiameter1 = gear1.working_pitch_diameter;
          workingPitchDiameter2 = gear2.working_pitch_diameter;
          sapDiameter1 = gear1.sap_diameter;
          sapDiameter2 = gear2.sap_diameter;
          eapDiameter1 = gear1.eap_diameter;
          eapDiameter2 = gear2.eap_diameter;

          // convert units for tabular display
          this.$handlers.eng.convertMesh(conn.mesh, undefined, this.$options.units);
          // console.log('conn.mesh', conn.mesh);  // DEV ONLY
        }

        // get complete part data
        const part1 = this.$store.getters.getPart(part1Id);
        const part2 = this.$store.getters.getPart(part2Id);

        // assign to local state
        this.mesh = {
          Id: conn.Id,
          part1, part2,
          // MESH DATA
          ratio, contactPlaneLength, backlashCircumferential,
          workingFacewidth, workingPressureAngleTransverse,
          workingPitchDiameter1, workingPitchDiameter2,
          sapDiameter1, sapDiameter2,
          eapDiameter1, eapDiameter2,
          // TABULAR DATA
          titleGear1: this.$handlers.model.partLabel(conn.parts[0]),  // collapse section title
          titleGear2: this.$handlers.model.partLabel(conn.parts[1]),  // collapse section title
          table: {
            log: this.$handlers.eng.tableLog(conn.mesh),
            common: this.$handlers.eng.tableMeshMacro(conn.mesh, this.$options.units),
            gear1: this.$handlers.eng.tableMeshGear1(conn.mesh, this.$options.units),
            gear2: this.$handlers.eng.tableMeshGear2(conn.mesh, this.$options.units),
          }
        };

        // render math typeset
        this.$nextTick(window.MathJax.typeset);
      },
      clearMesh() {
        // Clear mesh Id and tabular values.

        Object.assign(this.mesh, {
          Id: '',
          titleGear1: 'Gear 1',
          titleGear2: 'Gear 2',
          table: {
            log: [],
            common: [],
            gear1: [],
            gear2: [],
          }
        });
      },

      // EVENTS
      async handleTabOpened() {
        // Called after tab has opened.

        // initialize graphics
        this.newPixi();
        this.newPlot();
        this.onViewerResize();

        // if opening a connection, set dropdown and trigger change event
        if (this.openId) {
          this.conn = getObjectById(this.conns, this.openId);
          await this.changeConnection(this.conn);
        }
      },
      handleTabActive() {
        // Call viewer resize callback to prevent loss of pixi view if viewer size is changed while not on this tab.
        this.onViewerResize();
      },
      async changeConnection(conn) {
        // arg: Currently selected connection, otherwise empty string.

        if (conn) {
          this.loadingMesh = true;
          await this.queryMesh(conn);
          this.updatePixi();
          this.updatePlot();
          this.loadingMesh = false;
        } else {
          this.clearPixi();
          this.clearPlot();
          this.clearMesh();
        }
      },
      rotaterWheelCallback(e) {
        let deltaStep = pixi.normalizeDeltaY(e);
        this.rotater.value += this.rotaterStepSize * deltaStep;
      },
      onViewerResize() {
        // https://github.com/pixijs/pixi.js/wiki/v4-Tips,-Tricks,-and-Pitfalls#resizing-renderer
        // ==> Suggests to use 'screen' property in repositioning and scaling.  I briefly tried unsuccessfully.

        // Chart: resize display; CSS does not work
        this.chart.canvas.style.width = '100%';
        this.chart.canvas.style.height = '100%';
        this.chart.resize();  // resize renderer

        // Pixi: resize renderer
        const parent = this.pixi.view.parentNode;
        this.pixi.renderer.resize(parent.clientWidth, parent.clientHeight);

        // Pixi: resize graphics
        this.fitPixi();
      },

      // GRAPHICS - MESH
      newPixi() {
        // Create new pixi application and initialize graphics.

        this.pixi = new PIXI.Application({ view: this.$refs.pixi, transparent: true, antialias: true });

        // initialize gears
        this.pixiGear1 = new PIXI.Graphics();
        this.pixiGear2 = new PIXI.Graphics();
        this.pixi.stage.addChild(this.pixiGear1);
        this.pixi.stage.addChild(this.pixiGear2);

        // initialize mesh references
        this.pixiLoA = new PIXI.Graphics();
        this.pixi.stage.addChild(this.pixiLoA);

        // starter event listeners for resizing renderer
        const viewer = document.getElementById('viewer');
        this.resizeListener = new ResizeListener(viewer, this.onViewerResize);

        // listen for animate update
        this.pixi.ticker.add(() => {
          let err = this.rotater.value - (this.pixiGear1.rotation + this.rotate1);
          let errSpeed = err / 5;

          if (Math.abs(err) > 1e-5) {
            this.pixiGear1.rotation += errSpeed;
            this.pixiGear2.rotation = this.rotate2 - this.pixiGear1.rotation / this.mesh.ratio;
          }
        });

        // add wheel event to pixi and plot canvas for rotater
        this.$refs.pixi.addEventListener('wheel', this.rotaterWheelCallback);
        this.$refs.plot.addEventListener('wheel', this.rotaterWheelCallback);
      },
      fitPixi() {
        // Centers and scales the pixi gear mesh to provide a consistent view regardless of gear size.
        // Call BEFORE gears are drawn since the stage scale property is required for scaling linewidth.

        if (!this.mesh.Id) return;
        const view = this.pixi.view;

        // position gears to center pitch point at (0, 0)
        const [xCenter1, yCenter1] = [-this.mesh.workingPitchDiameter1 / 2, 0];
        const [xCenter2, yCenter2] = [this.mesh.workingPitchDiameter2 / 2, 0];
        this.pixiGear1.position.set(xCenter1, yCenter1);
        this.pixiGear2.position.set(xCenter2, yCenter2);

        // horizontally and vertically center the mesh pitch point
        const xStage = view.clientWidth / 2;
        const yStage = view.clientHeight / 2;

        // scale pixi stage to show 2.5 pitch lengths
        const pitchAngleTransverse1 = gear.pitchAngleTransverse1(this.mesh.part1.obj.geom.numberOfTeeth);
        const workingPitchLength = pitchAngleTransverse1 * this.mesh.workingPitchDiameter1 / 2;
        let scale = view.clientHeight / (2.5 * workingPitchLength);

        // DEV ONLY - scale to contact length
        // const contactPlaneHeight = this.mesh.contactPlaneLength * Math.cos(this.mesh.workingPressureAngleTransverse);
        // scale = view.clientHeight / (2 * contactPlaneHeight);

        // DEV ONLY - scale to 4X center distance
        // const zoomWidth = 2 * (this.mesh.workingPitchDiameter1 + Math.abs(this.mesh.workingPitchDiameter2));
        // scale = view.clientWidth / zoomWidth;

        // position (pan) and scale (zoom) the pixi stage
        this.pixi.stage.position.set(xStage, yStage);
        this.pixi.stage.scale.set(scale, scale);
      },
      clearPixi() {
        this.pixiGear1.clear();
        this.pixiGear2.clear();
        this.pixiLoA.clear();
      },
      updatePixi() {
        const gear1 = this.mesh.part1.obj;
        const gear2 = this.mesh.part2.obj;
        const unitAngle = 'rad';

        // gear geometry data
        const geom1 = gear1.geom;
        const geom2 = gear2.geom;

        // gear root data
        const finishedRoot1 = gear1.tooth.finishedRoot;
        const finishedRoot2 = gear2.tooth.finishedRoot;

        // common gear params
        const moduleNormal = geom1.moduleNormal;
        const pressureAngleNormal = geom1.pressureAngleNormal;

        // DEV ONLY
        // console.log('GEAR1', gear1);
        // console.log('GEAR2', gear2);
        // console.log('GEOM1', geom1);
        // console.log('GEOM2', geom2);

        // set rotations, positions, and scale
        this.updateRotater();
        this.fitPixi();

        // draw gear graphics
        pixi.gearGraphics(this.pixiGear1, geom1, finishedRoot1, unitAngle, this.$options.drawTeeth);
        pixi.gearGraphics(this.pixiGear2, geom2, finishedRoot2, unitAngle, this.$options.drawTeeth);

        // draw line of action
        this.loa = pixi.meshLineOfAction(this.pixiLoA,
          moduleNormal, pressureAngleNormal, geom1.helixAngle,
          geom1.numberOfTeeth, geom2.numberOfTeeth,
          this.mesh.workingPitchDiameter1, this.mesh.workingPitchDiameter2,
          this.mesh.workingFacewidth, this.mesh.workingPressureAngleTransverse,
          this.mesh.sapDiameter1, this.mesh.sapDiameter2, this.mesh.eapDiameter1, this.mesh.eapDiameter2,
          'rad');
      },
      updateRotater() {
        // WARNING: The so-called "circumferential" backlash computed by backend is actually a tangential backlash.
        //  - It uses the small angle approximation, so is close to accurate for small values of backlash.
        //  - The method used here to align teeth in contact is exact and does not use backend backlash.

        // region CLOCKING

        // gear #1 clocking; zero rotation corresponds to the tooth centered on x-axis
        const geom1 = {
          ...this.mesh.part1.obj.geom,
          profileShiftCoefficient: gear.profileShiftCoefficient2(this.mesh.part1.obj.geom)
        };
        const pitchToothThicknessTransverse1 = gear.toothThicknessTransverse3(this.mesh.workingPitchDiameter1, geom1);
        const pitchToothHalfAngle1 = gear.toothHalfAngleTransverse1(this.mesh.workingPitchDiameter1, pitchToothThicknessTransverse1);
        this.rotate1 = pitchToothHalfAngle1;

        // gear #2 clocking; zero rotation corresponds to the tooth centered on x-axis
        const geom2 = {
          ...this.mesh.part2.obj.geom,
          profileShiftCoefficient: gear.profileShiftCoefficient2(this.mesh.part2.obj.geom)
        };
        const pitchToothThicknessTransverse2 = gear.toothThicknessTransverse3(this.mesh.workingPitchDiameter2, geom2);
        const pitchToothHalfAngle2 = gear.toothHalfAngleTransverse1(this.mesh.workingPitchDiameter2, pitchToothThicknessTransverse2);
        const phi0 = this.internal ? 0 : Math.PI;  // angular offset of initial tooth
        this.rotate2 = phi0 - pitchToothHalfAngle1 / this.mesh.ratio - pitchToothHalfAngle2;

        // REFERENCE ONLY: gear #2 clocking computed from backlash
        // const pitchAngleTransverse2 = gear.pitchAngleTransverse1(geom2.numberOfTeeth);
        // const phiBacklash2 = this.mesh.backlashCircumferential / (this.mesh.workingPitchDiameter2 / 2);
        // const phiCenterBacklash = phi0 - pitchAngleTransverse2 / 2;  // centers tooth within backlash
        // this.rotate2 = phiCenterBacklash + phiBacklash2 / 2;

        // endregion

        // compute max rotater for X tooth passes
        const pitchAngleTransverse1 = gear.pitchAngleTransverse1(geom1.numberOfTeeth);
        this.rotater.max = this.rotater.cycles * pitchAngleTransverse1;
        this.rotater.value = 0;

        // compute values of mesh cycle
        let cycleValue;
        for (let i=0; i <= this.rotater.steps; i++) {
          cycleValue = this.rotater.cycles * i / this.rotater.steps;
          this.rotater.cyclesArr.push(cycleValue);
        }
      },

      // GRAPHICS - PLOT
      newPlot() {
        // Create a new chart.
        // Invert y-axis: https://stackoverflow.com/questions/33608040/how-do-i-invert-an-y-axis-using-chart-js-v2-0-dev

        let ctx = this.$refs.plot.getContext('2d');
        this.chart = new Chart(ctx, {
          type: 'line',
          data: {
            datasets: [],
          },
          options: {
            ...this.$defaults.chart.options,
            responsive: false,  // requires calling chart.resize() during to resize renderer to fit container
            scales: {
              xAxes: [{
                type: 'linear',
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: '(-)',
                  lineHeight: 1,
                  padding: 0
                }
              }],
              yAxes: [{
                scaleLabel: {
                  display: true,
                  labelString: '(-)',
                  lineHeight: 1,
                  padding: 0
                }
              }]
            },
            tooltips: {
              callbacks: {
                title: function(data) {
                  // round xLabel to three decimals
                  return Math.round(data[0].xLabel * 1000) / 1000;
                },
                label: function(tooltipItem, data) {
                  // round yLabel to three decimals
                  let label = data.datasets[tooltipItem.datasetIndex].label || '';
                  if (label) label += ': ';
                  label += Math.round(tooltipItem.yLabel * 1000) / 1000;
                  return label;
                }
              }
            }
          },
        });

        // configure chart defaults
        this.chart.options.elements.point.borderColor = this.$defaults.colors.background;
        this.chart.options.elements.point.backgroundColor = this.$defaults.colors.background;
        this.chart.options.elements.line.borderColor = this.$defaults.colors.background;
        this.chart.options.elements.line.tension = 0;
        this.chart.options.elements.line.fill = false;
      },
      clearPlot() {
        // remove any rotater callback
        this.plotRotaterCallback = () => {};

        // clear data and axis settings; .reset() and .clear() did not seem to help
        this.chart.data.datasets = [];
        this.chart.options.scales.xAxes[0].ticks.min = undefined;
        this.chart.options.scales.xAxes[0].ticks.max = undefined;
        this.chart.options.scales.yAxes[0].ticks.min = undefined;
        this.chart.options.scales.yAxes[0].ticks.max = undefined;
        this.chart.options.scales.yAxes[0].ticks.reverse = false;
        this.chart.options.scales.xAxes[0].scaleLabel.labelString = '(-)';
        this.chart.options.scales.yAxes[0].scaleLabel.labelString = '(-)';
        this.chart.options.legend.display = false;
        this.chart.options.tooltips.mode = 'nearest';
        this.chart.update();
      },
      updatePlot() {
        // Call when mesh is changed, but plot type is unchanged.

        // clear data in plot cache
        this.plotCache.contactLines = [];
        this.plotCache.sumOfContactLines = [];
        this.plotCache.contactRatioTransverse = [];
        this.plotCache.contactRatioAxial = [];
        this.plotCache.specificSliding1 = [];
        this.plotCache.specificSliding2 = [];

        // update existing plot type
        this.changePlotType(this.plotType);
      },

      // PLOT TYPES
      changePlotType(plotType) {
        // Selected plot type has changed.  Mesh is unchanged.
        // PLACEHOLDER {mesh stiffness} line plot as f{roll angle}; technically depends on load since nonlinear
        // PLACEHOLDER {microgeometry} line plots of conjugate lead relief and profile relief
        // PLACEHOLDER {microgeometry} contour plot of conjugate relief

        // clear any existing plot data
        this.clearPlot();

        // plot the selected plot type
        switch (plotType) {
          case this.$options.plotTypes.contactLines.type:
            this.plotContactLines();
            break;
          case this.$options.plotTypes.contactLinesSum.type:
            this.plotContactLinesSum();
            break;
          case this.$options.plotTypes.contactRatio.type:
            this.plotContactRatio();
            break;
          case this.$options.plotTypes.specificSliding.type:
            this.plotSpecificSliding();
            break;
        }

        // initialize rotater callback
        this.plotRotaterCallback(this.rotater.value);
      },
      computeContactData() {
        // if not cached, compute contact lines for all rotater values and their sum of contact lengths
        if (this.plotCache.sumOfContactLines.length === 0) {
          // initialize loop vars
          let rotaterValue, sumOfContactLineLengths;
          let contactLines, startPt, endPt, deltaX, deltaY;

          for (let i=0; i <= this.rotater.steps; i++) {
            // calculate rotation and cycle values
            rotaterValue = i * this.rotaterStepSize;

            // compute the active contact lines in the plane of action
            contactLines = this.loa.contactLines(rotaterValue);
            this.plotCache.contactLines.push(contactLines);

            // DEV ONLY
            // const pitchAngleTransverse = gear.pitchAngleTransverse1(this.mesh.part1.obj.geom.numberOfTeeth);
            // console.log('TOOTH PASS', rotaterValue / pitchAngleTransverse);
            // console.log('MESH CYCLE', this.rotater.cycles * i / this.rotater.steps);
            // console.log('CONTACT LINES', contactLines);
            // console.log('-------------');

            // sum the contact line lengths
            sumOfContactLineLengths = 0;
            contactLines.forEach(line => {
              startPt = line[0];
              endPt = line[line.length-1];
              deltaX = endPt.x - startPt.x;
              deltaY = endPt.y - startPt.y;
              sumOfContactLineLengths += Math.hypot(deltaX, deltaY);
            });

            // append to ordinate array
            this.plotCache.sumOfContactLines.push(sumOfContactLineLengths);

            // DEV ONLY
            // console.log('contactLines', contactLines);
            // console.log('sumOfContactLineLengths', this.plotData.sumOfContactLines);
          }
        }
      },
      plotContactLines() {
        // Plot contact lines in the plane of action.
        //  1. Calculates the contact points in the datum cross-section (pixi view).  Depends on rotater.value.
        //  2. The points in #1 serve as the y-intercept points for the contact lines.
        //  3. Plots contact lines with a slope per the base helix angle.

        // gear geometry data
        const geom1 = this.mesh.part1.obj.geom;
        const moduleTransverse = gear.moduleTransverse1({moduleNormal: geom1.moduleNormal, helixAngle: geom1.helixAngle});
        const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal: geom1.pressureAngleNormal, helixAngle: geom1.helixAngle});
        const theoreticalPitchDiameter1 = gear.theoreticalPitchDiameter1({numberOfTeeth: geom1.numberOfTeeth, moduleTransverse});
        const baseHelixAngle = gear.helixAngleBase1({pressureAngleTransverse, helixAngle: geom1.helixAngle});
        const baseDiameter1 = gear.baseDiameter1({theoreticalPitchDiameter: theoreticalPitchDiameter1, pressureAngleTransverse});
        const basePitchTransverse = gear.basePitchTransverse1({baseDiameter: baseDiameter1, numberOfTeeth: geom1.numberOfTeeth});
        const baseRadius1 = baseDiameter1 / 2;

        // facewidth coordinates
        const halfWorkingFacewidth = this.mesh.workingFacewidth / 2;
        const lineX = [-halfWorkingFacewidth, 0, halfWorkingFacewidth];

        // ordinate coordinates
        const sapY = Math.hypot(this.loa.sapPoint1.x, this.loa.sapPoint1.y) * Math.sign(this.loa.sapPoint1.y);
        const eapY = Math.hypot(this.loa.eapPoint1.x, this.loa.eapPoint1.y) * Math.sign(this.loa.eapPoint1.y);

        // region REFERENCE DATASETS

        // plot datum point
        this.chart.data.datasets.push({
          data: [{x: 0, y: 0}],
          label: 'datum pitch point',
          backgroundColor: pixi.MESH_LEGEND.pitch.color,
        });

        // plot horizontal line for SAP in plane of action
        const sapLineY = lineX.map(() => sapY);
        this.chart.data.datasets.push({
          data: coord.points(lineX, sapLineY),
          label: pixi.MESH_LEGEND.sap.label,
          borderColor: pixi.MESH_LEGEND.sap.color,
          backgroundColor: pixi.MESH_LEGEND.sap.color,
        });

        // plot horizontal line for EAP in plane of action
        const eapLineY = lineX.map(() => eapY);
        this.chart.data.datasets.push({
          data: coord.points(lineX, eapLineY),
          label: pixi.MESH_LEGEND.eap.label,
          borderColor: pixi.MESH_LEGEND.eap.color,
          backgroundColor: pixi.MESH_LEGEND.eap.color,
        });

        // endregion

        // region OPTIONAL DATASETS

        // // plot vertical line at (-) end of working facewidth
        // let loaLineY = [sapY, 0, eapY];
        // const leftFacewidthLineX = loaLineY.map(() => -halfWorkingFacewidth);
        // this.chart.data.datasets.push({
        //   data: coord.points(leftFacewidthLineX, loaLineY),
        //   label: pixi.MESH_LEGEND.loa.label,
        //   borderColor: pixi.MESH_LEGEND.loa.color,
        //   pointRadius: 0,
        // });
        //
        // // plot vertical line at (+) end of working facewidth
        // const rightFacewidthLineX = loaLineY.map(() => halfWorkingFacewidth);
        // this.chart.data.datasets.push({
        //   data: coord.points(rightFacewidthLineX, loaLineY),
        //   label: pixi.MESH_LEGEND.loa.label,
        //   borderColor: pixi.MESH_LEGEND.loa.color,
        //   pointRadius: 0,
        // });

        // endregion

        // region CONTACT LINES

        // calculate number of contact lines required to range of rotater
        const phiRange1 = this.rotater.max - this.rotater.min;
        const rollDistanceRange = baseRadius1 * phiRange1;
        const contactRatioTransverseRange = rollDistanceRange / basePitchTransverse;
        const contactRatioTransverse = this.mesh.contactPlaneLength / basePitchTransverse;
        const nContactLines = Math.ceil(contactRatioTransverseRange) + 2 * Math.ceil(contactRatioTransverse);
        const iContactEnd = 3;
        const iContactStart = iContactEnd - nContactLines;
        const contactSlope = Math.tan(baseHelixAngle);
        // console.log('nContactLines', nContactLines);  // DEV ONLY

        // plot contact lines
        let yIntercept, lineY, linePoints;
        for (let i=iContactStart; i < iContactEnd; i++) {
          // assuming pixi view is the datum cross-section, then contact points are y-intercepts of contact lines
          yIntercept = i * basePitchTransverse;
          lineY = lineX.map(x => contactSlope * x + yIntercept);

          // append to chart datasets
          linePoints = coord.points(lineX, lineY);
          this.chart.data.datasets.push({data: linePoints, label: 'contact line'});
          // console.log('i', i);  // DEV ONLY
        }

        // endregion

        // region CONFIG CHART

        // set axis limits to crop view of contact lines beyond the plane of action
        this.chart.options.scales.xAxes[0].ticks.min = -halfWorkingFacewidth;
        this.chart.options.scales.xAxes[0].ticks.max = halfWorkingFacewidth;
        this.chart.options.scales.yAxes[0].ticks.min = sapY;
        this.chart.options.scales.yAxes[0].ticks.max = eapY;
        this.chart.options.scales.yAxes[0].ticks.reverse = true;

        // set axis labels
        this.chart.options.scales.xAxes[0].scaleLabel.labelString = 'working facewidth (mm)';
        this.chart.options.scales.yAxes[0].scaleLabel.labelString = 'length of action (mm)';

        // update chart
        this.chart.update();

        // endregion

        // define rotater callback
        this.plotRotaterCallback = (phi1FromPitchPoint) => {
          // compute roll distance to contact, assuming phi=0 is pitch point
          let rollDistanceToContact = baseRadius1 * phi1FromPitchPoint;

          // get datasets for contact lines
          const contactDatasets = this.chart.data.datasets.slice(-nContactLines);

          // get dataset containing pitch point for phi=0
          const pitchPointY = contactDatasets[nContactLines-iContactEnd].data[1].y;
          const deltaRollDistance = (rollDistanceToContact - pitchPointY);

          // transform Y coordinate of contact lines
          contactDatasets.forEach(dataset => dataset.data.forEach(p => p.y += deltaRollDistance));

          // update chart
          this.chart.update();
        };
      },
      plotContactLinesSum() {
        this.computeContactData();

        // region PLOT DATASETS

        // plot sum of contact lines vs mesh cycle
        this.chart.data.datasets.unshift({
          data: coord.points(this.rotater.cyclesArr, this.plotCache.sumOfContactLines),
          label: 'sum of contact lines',
          lineTension: 0.2,
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
        });

        // plot vertical line and active point
        this.plotVerticalLine();
        this.plotPoints([{x: this.rotater.cyclesArr[0], y: this.plotCache.sumOfContactLines[0]}]);

        // set axis labels and tooltip mode
        this.chart.options.scales.xAxes[0].scaleLabel.labelString = 'mesh cycles (-)';
        this.chart.options.scales.yAxes[0].scaleLabel.labelString = 'sum of contact lines (mm)';
        this.chart.options.tooltips.mode = 'point';  // 'dataset'; NEITHER WORK AS EXPECTED

        // update chart
        this.chart.update();

        // endregion

        // define rotater callback
        this.plotRotaterCallback = (phi1FromPitchPoint) => {
          // resolve x-coordinate from gear #1 rotation
          const step = Math.round(phi1FromPitchPoint / this.rotaterStepSize);
          const x = this.rotater.cyclesArr[step];

          // move the point and vertical line
          this.chart.data.datasets[0].data[0] = {x: x, y: this.plotCache.sumOfContactLines[step]};  // point
          this.chart.data.datasets[1].data.forEach(p => p.x = x);  // vertical line
          this.chart.update();
        };
      },
      plotContactRatio() {
        // Instantaneous contact ratios are plotted for pitch point cross-sections in axial and transverse directions.
        // Contact ratios apply to cross-sections, not the whole plane of contact.

        this.computeContactData();

        // region PLOT DATASETS

        // if not cached, compute instantaneous contact ratios
        if (this.plotCache.contactRatioTransverse.length === 0) {
          let startPt, endPt, hasPitchContactTransverse, hasPitchContactAxial, hasPitchContactAxialFcn;

          // consider influence of helix sign
          const firstContactLine = this.plotCache.contactLines[0][0];
          const positiveContactSlope = firstContactLine[0].y < firstContactLine[1].y;

          // define fcn to detect if contact line intersects the datum axial section
          // does not use <= because spur gear would be non-zero sometimes
          if (positiveContactSlope) {
            hasPitchContactAxialFcn = (pt1, pt2) => pt1.y < 0 && 0 < pt2.y;  // RH
          } else {
            hasPitchContactAxialFcn = (pt1, pt2) => pt1.y > 0 && 0 > pt2.y;  // LH
          }

          this.plotCache.contactLines.forEach(lineSet => {
            // lineSet: array of active contact lines at an instance in time.

            // initialize loop values
            let nContactTransverse = 0;
            let nContactAxial = 0;

            lineSet.forEach(line => {
              [startPt, endPt] = line;

              // detect if contact line intersects the datum transverse section
              hasPitchContactTransverse = startPt.x <= 0 && 0 <= endPt.x;
              nContactTransverse += hasPitchContactTransverse ? 1 : 0;

              // detect if contact line intersects the datum axial section
              hasPitchContactAxial = hasPitchContactAxialFcn(startPt, endPt);
              nContactAxial += hasPitchContactAxial ? 1 : 0;

              // DEV ONLY
              // console.log('line', line);
              // console.log('hasPitchContactTransverse', hasPitchContactTransverse);
              // console.log('hasPitchContactAxial', hasPitchContactAxial);
              // console.log('--------------')
            });

            // append to cache
            this.plotCache.contactRatioTransverse.push(nContactTransverse);
            this.plotCache.contactRatioAxial.push(nContactAxial);
          });
        }

        const datasetOptionsCR = {
          lineTension: 0,
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
        };

        // plot axial contact ratio vs mesh cycle
        const colorAxial = '#053e98';
        this.chart.data.datasets.unshift({
          data: coord.points(this.rotater.cyclesArr, this.plotCache.contactRatioAxial),
          label: 'axial',
          borderColor: colorAxial,
          ...datasetOptionsCR
        });

        // plot transverse contact ratio vs mesh cycle
        const colorTransverse = '#a41010';
        this.chart.data.datasets.unshift({
          data: coord.points(this.rotater.cyclesArr, this.plotCache.contactRatioTransverse),
          label: 'transverse',
          borderColor: colorTransverse,
          ...datasetOptionsCR
        });

        // plot vertical line and active points
        this.plotVerticalLine();
        const ptX = this.rotater.cyclesArr[0];
        this.plotPoints([{x: ptX, y: this.plotCache.contactRatioAxial[0]}], undefined, colorAxial);
        this.plotPoints([{x: ptX, y: this.plotCache.contactRatioTransverse[0]}], undefined, colorTransverse);

        // set axis labels and tooltip mode
        this.chart.options.scales.xAxes[0].scaleLabel.labelString = 'mesh cycles (-)';
        this.chart.options.scales.yAxes[0].scaleLabel.labelString = 'contact ratio (-)';
        this.chart.options.tooltips.mode = 'x';  // 'dataset', 'point';

        // add legend
        this.chart.options.legend.display = true;
        this.chart.options.legend.labels.boxWidth = 2;
        this.chart.options.legend.labels.filter = (item) => {
          // remove legend entries with an undefined label
          return typeof item.text !== 'undefined';  // alternatively: ['axial', 'transverse'].includes(item.text)
        };

        // update chart
        this.chart.update();

        // endregion

        // define rotater callback
        this.plotRotaterCallback = (phi1FromPitchPoint) => {
          // resolve x-coordinate from gear #1 rotation
          const step = Math.round(phi1FromPitchPoint / this.rotaterStepSize);
          const x = this.rotater.cyclesArr[step];

          // update the points
          this.chart.data.datasets[0].data = [{x: x, y: this.plotCache.contactRatioTransverse[step]}];
          this.chart.data.datasets[1].data = [{x: x, y: this.plotCache.contactRatioAxial[step]}];

          // update the vertical line
          this.chart.data.datasets[2].data.forEach(p => p.x = x);
          this.chart.update();
        };
      },
      plotSpecificSliding() {
        // Line plot of specific sliding of the involute profiles at the datum cross-section, i.e. mid-facewidth.
        // Sliding properties are the same for all transverse cross-sections.

        // region PARAMS

        const geom1 = this.mesh.part1.obj.geom;
        const geom2 = this.mesh.part2.obj.geom;

        // common gear geometry
        const moduleTransverse = gear.moduleTransverse1({moduleNormal: geom1.moduleNormal, helixAngle: geom1.helixAngle});
        const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal: geom1.pressureAngleNormal, helixAngle: geom1.helixAngle});

        // gear #1 geometry
        const numberOfTeeth1 = geom1.numberOfTeeth;
        const theoreticalPitchDiameter1 = gear.theoreticalPitchDiameter1({numberOfTeeth: numberOfTeeth1, moduleTransverse});
        const baseDiameter1 = gear.baseDiameter1({theoreticalPitchDiameter: theoreticalPitchDiameter1, pressureAngleTransverse});

        // gear #2 geometry
        const numberOfTeeth2 = geom2.numberOfTeeth;
        const theoreticalPitchDiameter2 = gear.theoreticalPitchDiameter1({numberOfTeeth: numberOfTeeth2, moduleTransverse});
        const baseDiameter2 = gear.baseDiameter1({theoreticalPitchDiameter: theoreticalPitchDiameter2, pressureAngleTransverse});

        // endregion

        // region COMPUTE SLIDING

        // x-coordinate array for involute profile
        const activeDiameterArr1 = math.linspace(this.mesh.sapDiameter1, this.mesh.eapDiameter1, this.rotater.steps);

        // if not cached, compute specific sliding for mesh cycle
        if (this.plotCache.specificSliding1.length === 0) {
          let pressureAngleTransverseAtContactPoint1, pressureAngleTransverseAtContactPoint2;
          let slidingContactSpeed1, slidingContactSpeed2, slidingVelocity1, slidingVelocity2;
          let specificSliding1, specificSliding2;

          // dummy angular velocities
          const angularVelocity1 = 1;
          const angularVelocity2 = mesh.angularVelocityOutput1(angularVelocity1, this.mesh.ratio);

          // compute specific sliding along active profile
          activeDiameterArr1.forEach(activeDiameter1 => {
            // calculate pressure angles at contact point
            pressureAngleTransverseAtContactPoint1 = gear.diameter2PressureAngleTransverse(baseDiameter1, activeDiameter1);
            pressureAngleTransverseAtContactPoint2 = mesh.pressureAngleTransverseContactPoint1(
              this.mesh.workingPressureAngleTransverse, numberOfTeeth2, numberOfTeeth1, pressureAngleTransverseAtContactPoint1
            );

            slidingContactSpeed1 = mesh.slidingContactPointSpeed1(baseDiameter1, pressureAngleTransverseAtContactPoint1, angularVelocity1);
            slidingContactSpeed2 = mesh.slidingContactPointSpeed1(baseDiameter2, pressureAngleTransverseAtContactPoint2, angularVelocity2);
            slidingVelocity1 = mesh.slidingVelocity1(slidingContactSpeed1, slidingContactSpeed2);
            slidingVelocity2 = -slidingVelocity1;

            specificSliding1 = mesh.specificSliding1(slidingVelocity1, slidingContactSpeed1);
            specificSliding2 = mesh.specificSliding1(slidingVelocity2, slidingContactSpeed2);

            // append to arrays
            this.plotCache.specificSliding1.push(specificSliding1);
            this.plotCache.specificSliding2.push(specificSliding2);
          });
        }

        // endregion

        // region PLOT DATASETS

        const datasetOptions = {
          lineTension: 0,
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
        };

        const colorGear1 = '#a41010';
        const colorGear2 = '#053e98';
        const labelPart1 = this.$handlers.model.partLabel(this.mesh.part1);
        const labelPart2 = this.$handlers.model.partLabel(this.mesh.part2);

        // plot gear #2 specific sliding vs involute profile
        this.chart.data.datasets.unshift({
          data: coord.points(activeDiameterArr1, this.plotCache.specificSliding2),
          label: labelPart2,
          borderColor: colorGear2,
          ...datasetOptions
        });

        // plot gear #1 specific sliding vs involute profile
        this.chart.data.datasets.unshift({
          data: coord.points(activeDiameterArr1, this.plotCache.specificSliding1),
          label: labelPart1,
          borderColor: colorGear1,
          ...datasetOptions
        });

        // region PLOT REFERENCES

        // plot SAP points
        this.plotPoints([
          {x: activeDiameterArr1[0], y: this.plotCache.specificSliding1[0]},
          {x: activeDiameterArr1[0], y: this.plotCache.specificSliding2[0]}],
          'SAP', pixi.MESH_LEGEND.sap.color, 3
        );

        // plot EAP points
        const iEAP = activeDiameterArr1.length - 1;
        this.plotPoints([
            {x: activeDiameterArr1[iEAP], y: this.plotCache.specificSliding1[iEAP]},
            {x: activeDiameterArr1[iEAP], y: this.plotCache.specificSliding2[iEAP]}],
          'EAP', pixi.MESH_LEGEND.eap.color, 3
        );

        // plot pitch point
        this.plotPoints([{x: this.mesh.workingPitchDiameter1, y: 0}],
          'pitch point', pixi.MESH_LEGEND.pitch.color, 3
        );

        // endregion

        // region CONFIG

        // set axis labels and tooltip mode
        this.chart.options.scales.xAxes[0].scaleLabel.labelString = 'gear #1 diameter (mm)';
        this.chart.options.scales.yAxes[0].scaleLabel.labelString = 'specific sliding (-)';
        this.chart.options.tooltips.mode = 'x';  // 'dataset', 'point';

        // add legend
        this.chart.options.legend.display = true;
        this.chart.options.legend.labels.boxWidth = 2;
        this.chart.options.legend.labels.filter = (item) => {
          // filter legend entries for part labels only
          return [labelPart1, labelPart2].includes(item.text)
        };

        // update chart
        this.chart.update();

        // endregion

        // endregion

        // region ROTATER CALLBACK
        // PLACEHOLDER {specific sliding} needs to detect contact in plane of action, not just via diameter
        // const nDatasets = this.chart.data.datasets.length;
        // const basePitchTransverse = gear.basePitchTransverse1({baseDiameter: baseDiameter1, numberOfTeeth: numberOfTeeth1});
        // const rollDistanceWorkingPitch1 = gear.diameter2RollDistance(baseDiameter1, this.mesh.workingPitchDiameter1);
        // this.plotRotaterCallback = (phi1FromPitchPoint) => {
        //   // Plot vertical lines for each tooth pass.
        //   // WARNING: Expensive as a rotater callback.
        //
        //   // initialize; delete existing vertical lines
        //   this.chart.data.datasets.splice(nDatasets);
        //   const rollDistanceInit1 = phi1FromPitchPoint * baseDiameter1 / 2;
        //   let contactRollDistance1, contactDiameter1;
        //
        //   // PITCH-TO-EAP
        //   let inContact = true;
        //   let contactRollDistanceFromPitch = rollDistanceInit1;
        //   while (inContact) {
        //     contactRollDistance1 = rollDistanceWorkingPitch1 + contactRollDistanceFromPitch;
        //     contactDiameter1 = gear.rollDistance2Diameter(baseDiameter1, contactRollDistance1);
        //     inContact = contactDiameter1 <= this.mesh.eapDiameter1;
        //     if (inContact) {
        //       this.plotVerticalLine(contactDiameter1, this.$defaults.colors.backgroundAlpha);
        //       contactRollDistanceFromPitch += basePitchTransverse;
        //     }
        //   }
        //
        //   // PITCH-TO-SAP
        //   inContact = true;
        //   contactRollDistanceFromPitch = rollDistanceInit1;
        //   while (inContact) {
        //     contactRollDistanceFromPitch -= basePitchTransverse;
        //     contactRollDistance1 = rollDistanceWorkingPitch1 + contactRollDistanceFromPitch;
        //     contactDiameter1 = gear.rollDistance2Diameter(baseDiameter1, contactRollDistance1);
        //     inContact = contactDiameter1 >= this.mesh.sapDiameter1;
        //     if (inContact) {
        //       this.plotVerticalLine(contactDiameter1, this.$defaults.colors.backgroundAlpha);
        //     }
        //   }
        // };
        // endregion
      },
      plotVerticalLine(x=0, borderColor=this.$defaults.colors.border) {
        // Add vertical line to chart using the existing chart y-limits.

        // get existing y-limits
        this.chart.update();
        const yAxis = this.chart.scales['y-axis-0'];

        // plot vertical line
        this.chart.data.datasets.unshift({
          data: [{x: x, y: yAxis.min}, {x: x, y: yAxis.max}],
          borderColor: borderColor,
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
        });
      },
      plotPoints(ptsArr, label, color='#f100f6', radius=4) {
        // Add point(s) to chart, intended for use with vertical line and rotater interactivity.

          this.chart.data.datasets.unshift({
            data: ptsArr,
            label: label,
            pointRadius: radius,
            pointBackgroundColor: color,
            pointBorderColor: this.$defaults.colors.default,
            pointBorderWidth: 1,
            borderColor: this.$defaults.colors.none,
            borderWidth: 0,
          });
      },
    },
    beforeDestroy() {
      // stop ticker and destroy pixi
      this.pixi.stop();
      this.pixi.destroy(false, {children: true});

      // stop wheel listeners
      this.$refs.pixi.removeEventListener('wheel', this.rotaterWheelCallback);
      this.$refs.plot.removeEventListener('wheel', this.rotaterWheelCallback);

      // remove resize listener and observer
      this.resizeListener.destroy();
    }
  }
</script>

<style scoped src="../../assets/css/form.css"/>
<style scoped lang="scss" src="../../assets/css/layout-tab-pane-1-2.scss"/>
<style scoped lang="scss" src="../../assets/css/viewer-tab.scss"/>
<style scoped>
    .pane-grid-1-2 {
        grid-template-columns: 1fr 1fr;  /*override default style*/
    }
    .pane-1 {
        /*use with pixi-wrap*/
        display: block !important;
        padding-right: 1rem !important;
    }
    .pixi-wrap {
        /*use with pane-1 as block*/
        height: calc(100% - 40px);
    }
    .pixi {
        /*use with pixi-wrap*/
        margin-bottom: -.25em;
    }
    .mesh-select {
        width: calc(50% - .25em);
        margin-right: .25em;
        margin-bottom: .5em;
    }
    .mesh-select:last-of-type {
        margin-right: 0;
        margin-left: .25em;
    }
    #pane-1-message {
        position: absolute;
        width: calc(50% - 2em);
        text-align: center;
        top: 50%;
    }
</style>
